import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { InitialSetupService } from './initial-setup.service';
import { BadisOauthLoginService } from '../auth/badis-oauth-login.service';

@Injectable({
    providedIn: 'root'
})
export class SetupGuard implements CanActivate, CanActivateChild {

    constructor(
        private setupService: InitialSetupService,
        private authService: BadisOauthLoginService,
        private router: Router
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        return this.evaluateState(state.url);
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        return this.evaluateState(state.url);
    }

    private async evaluateState(url: string): Promise<boolean | UrlTree> {
        const isSetupRoute = url.startsWith('/setup');
        const isSetupReviewRoute = url.startsWith('/setup/review');
        const isSetupUnavailableRoute = url.startsWith('/setup/unavailable');
        const isDataMigrationRoute = url.startsWith('/setup/data-migration');
        const isAuthRoute = url.startsWith('/auth');
        const isAuthenticatedApplicationRoute = this.authService.isUserLoggedIn() && !isSetupRoute && !isAuthRoute;

        try {
            if (isAuthenticatedApplicationRoute) {
                if (this.setupService.shouldCheckDatabaseMigrationStatus()) {
                    const migrationStatus = await this.setupService.fetchDatabaseMigrationStatus().catch(() => undefined);
                    if (migrationStatus?.hasActiveJob) {
                        return this.router.createUrlTree(['/setup/data-migration/progress']);
                    }
                }

                const authenticatedBootstrapStatus = await this.setupService.fetchBootstrapStatus(false);
                if (this.isDatabaseUnavailable(authenticatedBootstrapStatus)) {
                    return this.router.createUrlTree(['/setup/unavailable']);
                }

                return true;
            }

            const bootstrapStatus = await this.setupService.fetchBootstrapStatus(isSetupRoute);

            if (this.isDatabaseUnavailable(bootstrapStatus)) {
                if (isSetupUnavailableRoute) {
                    return true;
                }

                return this.router.createUrlTree(['/setup/unavailable']);
            }

            if (!bootstrapStatus.bootstrapReady) {
                if (isSetupRoute && !isSetupReviewRoute && !isSetupUnavailableRoute) {
                    return true;
                }
                return this.router.createUrlTree(['/setup']);
            }

            if (isDataMigrationRoute || this.setupService.shouldCheckDatabaseMigrationStatus()) {
                const migrationStatus = await this.setupService.fetchDatabaseMigrationStatus().catch(() => undefined);
                if (migrationStatus?.hasActiveJob) {
                    if (isDataMigrationRoute) {
                        return true;
                    }

                    return this.router.createUrlTree(['/setup/data-migration/progress']);
                }
            }

            if (bootstrapStatus.requiresAdminSetupReview) {
                if (isAuthRoute) {
                    return true;
                }

                if (!this.authService.isUserLoggedIn()) {
                    return this.router.createUrlTree(['/auth'], {
                        queryParams: { returnUrl: '/setup/review' }
                    });
                }

                if (isSetupReviewRoute) {
                    return true;
                }

                return this.router.createUrlTree(['/setup/review']);
            }

            if (!bootstrapStatus.applicationSetupReady) {
                if (isSetupRoute && !isSetupReviewRoute && !isSetupUnavailableRoute) {
                    return true;
                }

                return this.router.createUrlTree(['/setup']);
            }

            if (bootstrapStatus.applicationSetupReady && isSetupRoute) {
                return this.router.createUrlTree(['/auth']);
            }
        } catch (error) {
            console.error('Unable to resolve initial setup status', error);
            if (!isSetupUnavailableRoute) {
                return this.router.createUrlTree(['/setup/unavailable']);
            }
        }

        return true;
    }

    private isDatabaseUnavailable(bootstrapStatus: { bootstrapState?: string; setupMode?: string; databaseConfigured?: boolean; databaseReachable?: boolean; canEditDatabaseConfiguration?: boolean }): boolean {
        return bootstrapStatus.bootstrapState === 'ConfiguredButUnavailable'
            || (bootstrapStatus.setupMode ?? '').toLowerCase() === 'databaseunavailable'
            || (!!bootstrapStatus.databaseConfigured
                && !bootstrapStatus.databaseReachable
                && bootstrapStatus.canEditDatabaseConfiguration === false);
    }

}
